import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

import OrderLine from '../components/OrderLine';
import StickyDialog from '../components/StickyDialog';

import { Revision } from '../types';

import { t } from '../i18n/i18n'; // 根据实际文件路径调整
import { beUpdateUserRevision, bePendUserRevision, useSound } from '../App';
import { useAppContext } from '../context/AppContext'; // 引入 useAppContext hook

//============================================================================
const RevisionCard = ({ revision, tustatus, refreshTrigger }) => {
    //========================================================================
    const cardRef = useRef(null); // 添加 ref 来引用卡片元素

    //========================================================================
    const [playSound, playSoundMultiple] = useSound('/goldcoin.mp3');

    //========================================================================
    const { 
            ServiceURLPrefix,
            localTimezone,
            blockPage, unblockPage,

            // isStatusModalOpen, setIsStatusModalOpen,
            // isRawMsgModalOpen, setIsRawMsgModalOpen
        } = useAppContext();

    //========================================================================
    const [isRawMsgModalOpen, setIsRawMsgModalOpen] = useState(false); // 控制 rawMsg 对话框
    const [isEnabled, setIsEnabled] = useState(revision.enable !== false); // 默认启用

    // 右键点击时间显示 rawMsg 对话框
    const handleRightClickTime = (e) => {
        e.preventDefault(); // 阻止默认右键菜单
        playSound();
        setIsRawMsgModalOpen(true);
    };

    // 双击时间切换 enable/disable 状态
    const handleDoubleClickTime = async () => {
        playSound();
        blockPage();
        revision.enable = !isEnabled;
        if( revision.enable && revision.status==="end" ) {//重新激活此revison
            revision.status= "prepare";
        }
        if (await beUpdateUserRevision(ServiceURLPrefix, revision)) {
            setIsEnabled(revision.enable);
            setLocalStatus(revision.status);
        } else {
            revision.enable = isEnabled; // 回滚
            revision.status= localStatus;
            console.error("[handleDoubleClickTime] Failed to update enable status");
        }
        unblockPage();
    };

    // 右键点击状态按钮，重置状态为 "prepare"
    const handleRightClickStatus = async (e) => {
        e.preventDefault(); // 阻止默认右键菜单
        playSound();
        blockPage();
        //revision.status= "prepare"; 恢复到prepare
        // 根据当前状态切换到上一个状态，不真实执行订单提交
        if (revision.status === 'prepare') {
            revision.status = 'active';
        } else if (revision.status === 'pending') {
            revision.status = 'prepare';
        } else if (revision.status === 'active') {
            revision.status = 'pending';
        }
        // 前后端操作是个事务整体，需要考虑回滚
        if (await beUpdateUserRevision(ServiceURLPrefix, revision)) {
            setLocalStatus(revision.status);  // 更新本地状态，触发重新渲染
        } else {
            revision.status= localStatus;
            console.error("重置状态失败");
        }
        unblockPage();
    };

    // 关闭 rawMsg 对话框
    const closeRawMsgModal = (e) => {
        console.debug("closeRawMsgModal...");
        if (e.target === e.currentTarget || e.target.classList.contains('close-btn')) {
            setIsRawMsgModalOpen(false);
        }
    };

    //========================================================================
    const [localStatus, setLocalStatus] = useState(revision.status); // 维护状态
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false); // 控制对话框显示

    // 被父组件的refreshTrigger触发强制更新
    useEffect(() => {
        //console.debug(" parent refreshTriggered revision... ", revision.status);
        setLocalStatus(revision.status);
        setIsEnabled(revision.enable !== false);
    }, [refreshTrigger, revision]);

    const handleStatusChange = () => {
        playSound();
        // 先打开确认对话框
        setIsStatusModalOpen(true);
    };

    //TODO:状态的确定应该放在后端，然后告诉前端情况
    const confirmStatusChange = async () => {
        // TODO：模式框拦截操作
        playSound();
        blockPage();
        setIsStatusModalOpen(false);
        let ns = '';
        // 根据当前状态切换到下一个状态
        if (revision.status === 'prepare') {
            if (await bePendUserRevision(ServiceURLPrefix, revision)) {
                playSoundMultiple(3);
                ns = 'pending';
            }
            else {
                unblockPage();
                return;
            }
        } else if (revision.status === 'pending') {
            ns = 'active';
        } else if (revision.status === 'active') {
            ns = 'end';
        }
        // 前后端操作是个事务整体，需要考虑回滚
        revision.status = ns;
        //if (await beUpdateUserRevision(ServiceURLPrefix, revision, true)) {
        if (await beUpdateUserRevision(ServiceURLPrefix, revision, false)) {
                setLocalStatus(ns);
        }
        else {
            revision.status = localStatus;
            console.error("[revision::confirmStatusChange] Rollback revision.status. Can't update into server.");
        }
        unblockPage();
    };

    const closeStatusModal = (e) => {
        console.debug("closeStatusModal...");
        if (e.target === e.currentTarget || e.target.classList.contains('close-btn')) {
            setIsStatusModalOpen(false);
        }
    };

    const cancelStatusChange = () => {
        setIsStatusModalOpen(false);
        //console.log('状态变更已取消');
    };

    const formattedTime = new Date(revision.timestamp).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: localTimezone,
    });

    // 新增条件判断
    const hasOrders = revision.orders && revision.orders.length > 0;

    const getCardClassName = () => {
        switch (revision.status) {
            case 'prepare':
                return 'revision-card-mask-prepare';
            case 'end':
                return 'revision-card-mask-end';
            case 'pending':
                return 'revision-card-mask-pending';
            case 'active':
                return 'revision-card-mask-active';
            default:
                return ''; // 默认没有蒙版
        }
    };

    // 添加键盘事件监听器，当按下 ESC 键时关闭模态框
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isRawMsgModalOpen) {
                    setIsRawMsgModalOpen(false);
                }
                if (isStatusModalOpen) {
                    setIsStatusModalOpen(false);
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isRawMsgModalOpen, isStatusModalOpen]);

    //========================================================================
    const [localOrders, setLocalOrders] = useState(revision.orders); //增加这么个本地拷贝是为了明确这里的修改要被更新
    // 删除订单的回调函数。由于我删除order这个确实会引起RevisionCard组件的渲染，所以我确实得从OrderLine.js组件
    // 回调RevisionCard组件的handleDeleteOrder（onDeleteOrder），并且还应该对orders用useState机制来给
    // RevisionCard组件明确要因为orders的变化触发渲染。
    const handleDeleteOrder = async (orderToDelete) => {
        playSound();
        blockPage();
        // 删除本地状态中的订单
        const updatedOrders = localOrders.filter(order => order.id !== orderToDelete.id);
        // 直接修改而未调用更上级回调。RevisionCard.js内的revision是父组件传入的引用，我直接把新的orders给到这个revision，那么父组件里的
        // revision也就变，它们都是指向同一个内存。而且因为revision的渲染由RevisionCard.js组件自己负责，并不需要它的父组件渲染，所以，所以偷个懒。
        // react中父组件子组件等多层组件的渲染都是在一个线程，所以虽然这里有产生新的orders，而order组件内部的修改也都会在的。
         // 前后端操作是个事务整体
         revision.orders= updatedOrders
         //if( await beUpdateUserRevision(ServiceURLPrefix, revision, true) ) {
         if( await beUpdateUserRevision(ServiceURLPrefix, revision, false) ) {
                setLocalOrders(updatedOrders);  // 更新 本地 localOrders 状态，并触发重绘
        }
        else {
            revision.orders= localOrders;
            console.error("[revision::handleDeleteOrder] Rollback orders. Can't update into server.");
        }
        unblockPage();
    };

    //========================================================================
    const [isEditingDiff, setIsEditingDiff] = useState(false);
    const [localDiff, setLocalDiff] = useState(revision.diff);
    const [isEditingPosition, setIsEditingPosition] = useState(false);
    const [localPosition, setLocalPosition] = useState(revision.position);//单位USDT
   
    // 处理 diff 的编辑逻辑
    const handleDoubleClickDiff = () => {
      playSound();
      setIsEditingDiff(true);
    };
    const handleChangeDiff = (e) => {
      setLocalDiff(e.target.value);//e.target.value是字符串
    };    
    const handleKeyDownDiff = (e) => {
      if (e.key === 'Enter') {
        handleConfirmDiff();
      } else if (e.key === 'Escape') {
        handleCancelDiff();
      }
    };
    const handleConfirmDiff = async() => {
        // playSound();
        // blockPage();
        // setIsEditingDiff(false);
        // // 前后端操作是个事务整体
        // let oldvalue= revision.diff;//同tradingunitstreamcontainer.js里的leverage类系，要考虑cancel编辑和db保存失败的两种回滚。localLeverage被setLocalLeverage不停的设置，这里另外弄了个oldvalue来保存原始值给回滚备用。
        // revision.diff = localDiff;
        // if( await beUpdateUserRevision(ServiceURLPrefix, revision) )
        //     setLocalDiff(localDiff);//FIXME：到底有没有必要执行这句？执行了也还是没有马上更新显示！
        // else
        //     revision.diff = oldvalue;
        // unblockPage();
        // 还是同order一样，不要实时修改服务端更新了。在App.js的makeFinalRev里需要同order一样处理。
        playSound();
        setIsEditingDiff(false);
        revision.diff = localDiff;
    };
    const handleCancelDiff = () => {
      playSound();
      setIsEditingDiff(false);
      setLocalDiff(revision.diff); // 恢复旧值
    };
    const handleBlurDiff = (e) => {
        // 如果用户点击了确认或取消按钮，则阻止 onBlur 事件的触发
        if (e.relatedTarget && (e.relatedTarget.classList.contains('confirm-button') || e.relatedTarget.classList.contains('cancel-button'))) {
            return;
        }
        handleCancelDiff();
    };
    
    // 处理 position 的编辑逻辑（与 diff 类似）
    const handleDoubleClickPosition = () => {
      playSound();
      setIsEditingPosition(true);
    };
    const handleChangePosition = (e) => {
      setLocalPosition(e.target.value);//e.target.value是字符串
    };    
    const handleKeyDownPosition = async(e) => {
      if (e.key === 'Enter') {
        await handleConfirmPosition();
      } else if (e.key === 'Escape') {
        handleCancelPosition();
      }
    };
    const handleConfirmPosition = async() => {
        // playSound();
        // blockPage();
        // setIsEditingPosition(false);
        // // 前后端操作是个事务整体
        // let oldvalue= revision.position;//同tradingunitstreamcontainer.js里的leverage类系，要考虑cancel编辑和db保存失败的两种回滚。localLeverage被setLocalLeverage不停的设置，这里另外弄了个oldvalue来保存原始值给回滚备用。
        // revision.position = localPosition;
        // if( await beUpdateUserRevision(ServiceURLPrefix, revision) )
        //     setLocalPosition(localPosition);//FIXME：到底有没有必要执行这句？执行了也还是没有马上更新显示！
        // else
        //     revision.position = oldvalue;
        // unblockPage();
        // 还是同order一样，不要实时修改服务端更新了。在App.js的makeFinalRev里需要同order一样处理。
        playSound();
        setIsEditingPosition(false);
        revision.position = localPosition;
    };
    const handleCancelPosition = () => {
      playSound();
      setIsEditingPosition(false);
      setLocalPosition(revision.position);
    };
    const handleBlurPosition = async (e) => {
        // 如果用户点击了确认或取消按钮，则阻止 onBlur 事件的触发
        if (e.relatedTarget && (e.relatedTarget.classList.contains('confirm-button') || e.relatedTarget.classList.contains('cancel-button'))) {
            return;
        }
        handleCancelPosition();
    };

    //========================================================================
    const getRevisionJson = (revision) => {
        // 创建一个副本，避免直接修改原始对象
        const { rawMsg, URL, ...revisionLeft } = revision;
        // 将副本转换为格式化的 JSON 字符串
        return JSON.stringify(revisionLeft, null, 2);
    };

    //const baseURL = typeof revision?.URL === 'string' ? revision.URL.split('?senderid=')[0] : '';
    // const messageIdSuffix = revision?.rawMsg?.split('$mes@sageid:&')[1] || '';
    // const finalURL = baseURL + messageIdSuffix;
    // const rawMessagePrefix = typeof revision?.rawMsg === 'string'
    //     ? (revision.rawMsg.split('$mes@sageid:&')[0] || '').replace(/\\n/g, '\n')
    //     : '';

    const finalURL = revision.URL;
    const rawMessagePrefix = revision.rawMsg;

    //========================================================================
    return (
        <div ref={cardRef} className={`revision-card ${!isEnabled ? "disabled" : ""}`}>
            {revision.isNew && (
                <StickyDialog
                    position={{
                        x: cardRef.current ? cardRef.current.getBoundingClientRect().left : 0,
                        y: cardRef.current ? cardRef.current.getBoundingClientRect().bottom : 0
                    }}
                    content={rawMessagePrefix}
                    onClose={() => {
                        revision.isNew = false;
                        //console.log('对话框关闭');
                    }}
                />
            )}

            <table>
                <thead>
                    <tr>
                        {/* 修改时间列跨度 */}
                        <th 
                            colSpan={hasOrders ? 2 : 3}
                            onDoubleClick={handleDoubleClickTime} 
                            onContextMenu={handleRightClickTime}
                            style={{ cursor: 'pointer' }}
                        >
                            {formattedTime}
                        </th>
                        {/* 条件渲染状态按钮列 */}
                        {hasOrders && (
                        <th className="revision-status" rowSpan="2">
                            {revision.status !== 'end' && (
                                <button
                                    className={`status-button ${revision.status} ${!isEnabled ? "disabled" : ""}`}
                                    onClick={isEnabled ? handleStatusChange : null}
                                    onContextMenu={handleRightClickStatus}  // 新增：右键处理
                                >
                                    {revision.status === 'prepare'
                                        ? t('pending')
                                        : revision.status === 'pending'
                                            ? t('active')
                                            : t('end')}
                                </button>
                            )}
                        </th>
                        )}
                    </tr>

                    {/* 条件渲染参数行 */}
                    {hasOrders && (
                    <tr>

                        {isEditingPosition ? (<></>) :
                            isEditingDiff ? (
                                <th colSpan="2" style={{ textAlign: "left" }}>
                                    <span className="editable-field" onDoubleClick={handleDoubleClickDiff}>{t("Diff")}:</span>
                                    <span>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={localDiff}
                                            onChange={handleChangeDiff}
                                            onKeyDown={handleKeyDownDiff}
                                            onBlur={handleBlurDiff}
                                            autoFocus
                                            className="no-arrows"
                                        />
                                        <button onClick={handleConfirmDiff} className="confirm-button">✓</button>
                                        <button onClick={handleCancelDiff} className="cancel-button">✗</button>
                                    </span>
                                </th>
                            ) : (
                                <th>
                                    <span className="editable-field" onDoubleClick={handleDoubleClickDiff}>{t("Diff")}:</span>
                                    <span
                                        className="editable-field"
                                        onDoubleClick={handleDoubleClickDiff}
                                    >
                                        {revision.diff}
                                    </span>
                                </th>
                            )}

                        {isEditingDiff ? (<></>) :
                            isEditingPosition ? (
                                <th colSpan="2" style={{ textAlign: "left" }}>
                                    <span className="editable-field" onDoubleClick={handleDoubleClickPosition}>{t("Position")}:</span>
                                    <span>
                                        <input
                                            type="number"
                                            value={localPosition}
                                            onChange={handleChangePosition}
                                            onKeyDown={handleKeyDownPosition}
                                            onBlur={handleBlurPosition}
                                            autoFocus
                                            className="no-arrows"
                                        />
                                        <button onClick={handleConfirmPosition} className="confirm-button">✓</button>
                                        <button onClick={handleCancelPosition} className="cancel-button">✗</button>
                                    </span>
                                </th>
                            ) : (
                                <th>
                                    <span className="editable-field" onDoubleClick={handleDoubleClickPosition}>{t("Position")}:</span>
                                    <span
                                        className="editable-field"
                                        onDoubleClick={handleDoubleClickPosition}
                                    >
                                        {revision.position}
                                    </span>
                                </th>
                            )}

                    </tr>
                    )}
                </thead>

                {/* 条件渲染订单列表 */}
                {hasOrders && (
                <tbody>
                    {revision.orders?.map(order => (
                        <OrderLine
                            key={`${revision.TUID}${revision.id}${order.id}`}
                            order={order}
                            revisionStatus={revision.status}
                            onDeleteOrder={handleDeleteOrder} // 传递删除回调
                        />
                    ))}
                </tbody>
                )}
            </table>

            {/* 添加一个蒙版 */}
            <div className={`revision-card-mask ${
                tustatus === "disable" || !isEnabled ? "revision-card-mask-end" : getCardClassName()
            }`}></div>

            {/* 使用 Portal 的状态对话框 */}
            {isStatusModalOpen && createPortal(
                <div className="modal-overlay" onClick={closeStatusModal}>
                    <div className="modal status-modal">
                        <button className="close-btn" onClick={closeStatusModal}>×</button>
                        <p style={{ fontSize: '20px', color: 'red', fontWeight: 'bold' }}>{t('Modify')}</p>
                        <p>{t('AreYouSure?')}</p>
                        <div className="modal-buttons">
                            <button onClick={confirmStatusChange} className="confirm-button">{t('Confirm')}</button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <button onClick={cancelStatusChange} className="cancel-button">{t('Cancel')}</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* 使用 Portal 的 rawMsg 对话框 */}
            {isRawMsgModalOpen && createPortal(
                <div className="modal-overlay" onClick={closeRawMsgModal}>
                    <div className="modal">
                        <button className="close-btn" onClick={closeRawMsgModal}>×</button>
                        <div className="rawmsg-content">
                            {/* <div>
                                <b>Revision Details</b> | <label>TUID: {revision.TUID} | ID: {revision.id}</label>
                            </div> */}
                            <div className="url-section">
                                <label>URL:</label>
                                <a href={finalURL || '#'} target="_blank" rel="noopener noreferrer">
                                    {finalURL || '[无效链接]'}
                                </a>
                            </div>
                            <div className="rawmsg-section">
                                <textarea readOnly value={rawMessagePrefix} />
                                <textarea readOnly value={getRevisionJson(revision)} />
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

//============================================================================
RevisionCard.propTypes = {
    revision: Revision.isRequired,
    tustatus: PropTypes.string.isRequired,
    refreshTrigger: PropTypes.any,
};

//============================================================================
export default RevisionCard;